import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { ChangeAccessLevelConfirmDialog } from "@components/project/change-access-level-confirm-dialog";
import { AccessLevelDropdown } from "@components/access-level/access-level-dropdown";
import { SdbProject } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  updateProjectDetails,
  updateSnapshotDetails,
} from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useCallback, useState } from "react";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { MenuItem, Text, LockIcon, useToast } from "@flat-ui";
import { Snapshot } from "@custom-types/base-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  ChangeProjectDetailsEvents,
  SnapshotEvents,
} from "@utils/track-event/track-event-list";

interface CommonProps {
  /** Optional callback fired when the menu closes. Called after successful access level change. */
  onMenuClose?: () => void;

  /** Optional custom callback for access level change. If not provided, dispatches updateProjectDetails directly. */
  onAccessLevelChange?: (
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ) => void;
}

interface ProjectProps extends CommonProps {
  /** Indicates whether the menu is for a project */
  type: "project";

  /** Project object containing access level information. Used to extract current access level and project ID for updates. */
  entity?: SdbProject;
}

interface SnapShotProps extends CommonProps {
  /** Indicates whether the menu is for a snapshot */
  type: "snapshot";

  /** Snapshot object containing access level information. Used to extract current access level and snapshot ID for updates. */
  entity?: Snapshot;
}

type Props = ProjectProps | SnapShotProps;

/**
 * Menu button that allows changing access level.
 * Renders a FaroTextMenuButton with AccessLevelDropdown.
 */
export function AccessLevelMenuButton({
  type,
  entity,
  onMenuClose,
  onAccessLevelChange,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const bulkMode = useBulkActionsMode();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();

  const [selectedAccessLevel, setSelectedAccessLevel] = useState<
    FaroCoreApiTypes.ProjectAccessLevel | undefined
  >();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useTrackEvent();

  // Force "collapsed" mode for single-project menus to ensure dark text on white background.
  const mode = entity ? "collapsed" : bulkMode;

  const handleBulkAccessLevelChange = useCallback(
    (accessLevel: FaroCoreApiTypes.ProjectAccessLevel) => {
      // If custom callback provided, use it (e.g., for bulk actions with confirmation dialog)
      if (onAccessLevelChange) {
        onAccessLevelChange(accessLevel);
        return;
      }

      // Close the menu after selection
      onMenuClose?.();
    },
    [onMenuClose, onAccessLevelChange]
  );

  function handleSelectAccessLevel(
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ): void {
    setSelectedAccessLevel(accessLevel);
    setIsDialogOpen(true);
  }

  async function handleConfirm(): Promise<void> {
    if (!selectedAccessLevel || !entity) {
      return;
    }

    trackEvent({
      name:
        type === "project"
          ? ChangeProjectDetailsEvents.changeProjectAccessLevel
          : SnapshotEvents.editSnapshot,
      props: { accessLevel: selectedAccessLevel },
    });

    setIsLoading(true);
    try {
      if (type === "project") {
        await dispatch(
          updateProjectDetails({
            projectId: entity.id,
            payload: { accessLevel: selectedAccessLevel },
          })
        ).unwrap();
      } else if (type === "snapshot") {
        await dispatch(
          updateSnapshotDetails({
            snapshotId: entity.id,
            payload: { accessLevel: selectedAccessLevel },
          })
        ).unwrap();
      }

      openToast({
        title: `${type} access level changed.`,
      });
    } catch (error) {
      handleErrorWithToast({
        id: `updateProjectDetails-accessLevel-${Date.now().toString()}`,
        title: `Could not change ${type} access level`,
        error,
      });
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
      onMenuClose?.();
    }
  }

  const config = getBulkActionButtonConfig(
    mode,
    `Access level of selected ${type}s`
  );

  // This is the case for single entity (project or snapshot), where we can show the current access level in the dropdown
  if (entity) {
    return (
      <>
        <MenuItem label="Access level" Icon={LockIcon} subMenuTrigger="click">
          <AccessLevelDropdown
            type={type}
            currentAccessLevel={entity.accessLevel}
            onSelectAccessLevel={handleSelectAccessLevel}
          />
        </MenuItem>

        {selectedAccessLevel && (
          <ChangeAccessLevelConfirmDialog
            type={type}
            accessLevel={selectedAccessLevel}
            isOpen={isDialogOpen}
            isLoading={isLoading}
            onConfirm={() => void handleConfirm()}
            onClose={() => setIsDialogOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    /**
     *  For bulk actions, we use the FaroTextMenuButton with the same dropdown,
     *  but without passing the current access level (as it may differ between entities)
     */
    <FaroTextMenuButton
      buttonTitle={
        config.shouldShowText ? (
          <Text variant="buttonM">Access level</Text>
        ) : undefined
      }
      tooltipText={config.tooltipText}
      shouldShowEndIcon={false}
      rippleColor={config.rippleColor}
      startIcon={<LockIcon style={{ width: "18px", height: "18px" }} />}
      menuPosition={{
        anchorOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      }}
      buttonSx={() => config.sx}
    >
      <AccessLevelDropdown
        type={type}
        onSelectAccessLevel={handleBulkAccessLevelChange}
      />
    </FaroTextMenuButton>
  );
}
